import { useEffect, useMemo, useState } from 'react';
import { IoCardOutline, IoCheckmark, IoPersonOutline } from 'react-icons/io5';
import {
  BASE_TEAM_PRICE_CENTAVOS,
  BASE_TEAM_SEATS,
  EXTRA_SEAT_PRICE_CENTAVOS,
  INBOUND_PACK_PRICE_CENTAVOS,
  INBOUND_PACK_VOLUME,
  packPricing,
  YEARLY_DISCOUNT,
} from '@x9/billing';
import AcceptedCardBrands from '../components/AcceptedCardBrands';
import AsaasSecureNote from '../components/AsaasSecureNote';
import BillingAddressFields, {
  type BillingAddressValue,
} from '../components/BillingAddressFields';
import FieldVerifiedIcon from '../components/FieldVerifiedIcon';
import HwyahProductFooter from '../components/HwyahProductFooter';
import X9Logo from '../components/X9Logo';
import { formatCep, formatCpfCnpj, isValidCpfCnpj } from '../lib/billing/brIdentity';
import { getFirebaseAuth } from '../config/firebase';
import { getPrivacyUrl, getTermsUrl } from '../constants/legalUrls';
import { invalidateBillingSummaryCache } from '../hooks/useBillingSummary';
import {
  callableErrorMessage,
  fetchBillingSubscription,
  startCardCheckout,
} from '../services/api';

type Interval = 'MONTHLY' | 'YEARLY';
type Step = 'details' | 'payment' | 'confirm';

type Props = {
  interval: Interval;
  /** Seat capacity (default base Equipe = 3). Prefer over seatPacks. */
  seatCount?: number;
  /** @deprecated Prefer seatCount. */
  seatPacks?: number;
  volumePacks?: number;
  /** Already verified recovery email — required before this page is shown. */
  verifiedEmail: string;
  onBack: () => void;
  onPending: (subscriptionId: string) => void;
  /** Existing monthly Equipe — checkout updates that plan, no new subscription. */
  onPackUpdated?: () => void;
  onSwitchToYearly: () => void;
};

const STEPS: Step[] = ['details', 'payment', 'confirm'];

function brl(cents: number): string {
  return (cents / 100).toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  });
}

function digitsOnly(value: string): string {
  return value.replace(/\D/g, '');
}

function formatBrPhone(value: string): string {
  const d = digitsOnly(value).replace(/^55/, '');
  if (d.length === 11) return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
  if (d.length === 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
  return d;
}

function CheckoutError({
  message,
  className = '',
}: {
  message: string | null;
  className?: string;
}) {
  if (!message) return null;
  return (
    <p
      className={`rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 ${className}`}
      role="alert"
    >
      {message}
    </p>
  );
}

function formatCardNumber(value: string): string {
  return digitsOnly(value)
    .slice(0, 19)
    .replace(/(\d{4})(?=\d)/g, '$1 ')
    .trim();
}

function formatExpiryInput(value: string): string {
  const d = digitsOnly(value).slice(0, 4);
  if (d.length <= 2) return d;
  return `${d.slice(0, 2)}/${d.slice(2)}`;
}

function parseExpiry(value: string): { month: string; year: string } | null {
  const cleaned = value.replace(/\s/g, '');
  const m = cleaned.match(/^(\d{2})\s*[\/]?\s*(\d{2}|\d{4})$/);
  if (!m) return null;
  const month = m[1];
  let year = m[2];
  if (year.length === 2) year = `20${year}`;
  const mi = Number(month);
  if (mi < 1 || mi > 12) return null;
  return { month: month.padStart(2, '0'), year };
}

function stepLabel(step: Step): string {
  if (step === 'details') return 'Seus dados';
  if (step === 'payment') return 'Pagamento';
  return 'Confirmar';
}

const STEP_ICONS = {
  details: IoPersonOutline,
  payment: IoCardOutline,
  confirm: IoCheckmark,
} as const;

function CheckoutSteps({
  step,
  stepIndex,
  variant = 'labels',
}: {
  step: Step;
  stepIndex: number;
  variant?: 'labels' | 'icons';
}) {
  return (
    <ol className={variant === 'icons' ? 'flex shrink-0 gap-1.5' : 'flex gap-2 text-xs font-medium'}>
      {STEPS.map((s, i) => {
        const active = s === step;
        const done = i < stepIndex;
        const tone = active
          ? 'bg-primary text-white'
          : done
            ? 'bg-muted text-foreground'
            : 'bg-accent text-muted-foreground';
        if (variant === 'icons') {
          const Icon = STEP_ICONS[s];
          return (
            <li key={s}>
              <span
                className={`flex h-8 w-8 items-center justify-center rounded-full ${tone}`}
                title={stepLabel(s)}
              >
                <Icon className="h-4 w-4" aria-hidden />
                <span className="sr-only">{stepLabel(s)}</span>
              </span>
            </li>
          );
        }
        return (
          <li key={s} className={`flex-1 rounded-full px-2 py-2 text-center ${tone}`}>
            {i + 1}. {stepLabel(s)}
          </li>
        );
      })}
    </ol>
  );
}

function CheckoutOrderSummary({
  interval,
  pricing,
  amount,
  yearlyAmount,
  onSwitchToYearly,
  isPackUpdate,
}: {
  interval: Interval;
  pricing: ReturnType<typeof packPricing>;
  amount: number;
  yearlyAmount: number;
  onSwitchToYearly: () => void;
  isPackUpdate?: boolean;
}) {
  const extraSeats = Math.max(0, pricing.seatCount - BASE_TEAM_SEATS);
  const period = interval === 'YEARLY' ? 'ano' : 'mês';
  const yearlyMonthlyEquiv = Math.round(yearlyAmount / 12);
  const yearlyDiscountCents = interval === 'YEARLY'
    ? Math.round(pricing.monthlyCents * 12) - amount
    : 0;

  return (
    <div className="space-y-6">
      <div className="hidden items-center gap-2 text-foreground lg:flex">
        <X9Logo className="h-7 w-7" title="X9" />
        <span className="text-sm font-semibold tracking-wide">X9 Equipe</span>
      </div>

      <div>
        <p className="text-sm text-muted-foreground">
          {interval === 'YEARLY' ? 'Assinatura anual' : 'Assinatura mensal'}
        </p>
        <p className="mt-1 text-3xl font-bold tracking-tight text-foreground lg:text-4xl">
          {brl(amount)}
          <span className="ml-1 text-base font-medium text-muted-foreground">
            /{period}
          </span>
        </p>
        {interval === 'YEARLY' && (
          <p className="mt-1 text-sm text-muted-foreground">
            Equivale a {brl(yearlyMonthlyEquiv)}/mês · {Math.round(YEARLY_DISCOUNT * 100)}% de
            desconto
          </p>
        )}
      </div>

      <ul className="space-y-3 text-sm">
        {pricing.seatCount > 0 && (
          <li className="flex justify-between gap-4">
            <span className="text-foreground">
              Equipe · {BASE_TEAM_SEATS} assentos
            </span>
            <span className="shrink-0 text-muted-foreground">
              {brl(interval === 'YEARLY'
                ? Math.round(BASE_TEAM_PRICE_CENTAVOS * 12)
                : BASE_TEAM_PRICE_CENTAVOS)}
            </span>
          </li>
        )}
        {extraSeats > 0 && (
          <li className="flex justify-between gap-4">
            <span className="text-foreground">
              {extraSeats} assento{extraSeats === 1 ? '' : 's'} extra
            </span>
            <span className="shrink-0 text-muted-foreground">
              {brl((interval === 'YEARLY' ? extraSeats * EXTRA_SEAT_PRICE_CENTAVOS * 12 : extraSeats * EXTRA_SEAT_PRICE_CENTAVOS))}
            </span>
          </li>
        )}
        {pricing.volumePacks > 0 && (
          <li className="flex justify-between gap-4">
            <span className="text-foreground">
              {pricing.volumePacks} pacote{pricing.volumePacks === 1 ? '' : 's'} de volume
              <span className="block text-xs text-muted-foreground">
                +{pricing.volumePacks * INBOUND_PACK_VOLUME} contribuições/mês
              </span>
            </span>
            <span className="shrink-0 text-muted-foreground">
              {brl(interval === 'YEARLY'
                ? pricing.volumePacks * INBOUND_PACK_PRICE_CENTAVOS * 12
                : pricing.volumePacks * INBOUND_PACK_PRICE_CENTAVOS)}
            </span>
          </li>
        )}
        {yearlyDiscountCents > 0 && (
          <li className="flex justify-between gap-4 text-emerald-700">
            <span>Desconto anual</span>
            <span className="shrink-0">−{brl(yearlyDiscountCents)}</span>
          </li>
        )}
      </ul>

      <div className="flex justify-between border-t border-border pt-4 text-sm font-semibold text-foreground">
        <span>{isPackUpdate ? 'Próxima fatura' : 'Total hoje'}</span>
        <span>{brl(amount)}</span>
      </div>

      <p className="text-xs text-muted-foreground">
        {isPackUpdate
          ? 'Os assentos extras valem agora. A diferença entra na próxima fatura — não cobramos de novo o período já pago.'
          : 'Cobrança recorrente no cartão. Cancele quando quiser — o acesso segue até o fim do período já pago.'}
        {interval === 'YEARLY'
          ? ' Tempo restante de um mensal ativo, se houver, é somado ao anual.'
          : ''}
      </p>

      {interval === 'MONTHLY' && (
        <div className="rounded-lg bg-card p-4">
          <p className="text-sm font-semibold text-foreground">Economize no anual</p>
          <p className="mt-1 text-sm text-muted-foreground">
            {brl(yearlyAmount)}/ano · {brl(yearlyMonthlyEquiv)}/mês
          </p>
          <button
            type="button"
            className="mt-3 text-sm font-semibold text-foreground underline underline-offset-2"
            onClick={onSwitchToYearly}
          >
            Trocar para plano anual
          </button>
        </div>
      )}
    </div>
  );
}

export default function CheckoutPage({
  interval,
  seatCount,
  seatPacks,
  volumePacks = 0,
  verifiedEmail,
  onBack,
  onPending,
  onPackUpdated,
  onSwitchToYearly,
}: Props) {
  const authPhone = useMemo(
    () => getFirebaseAuth().currentUser?.phoneNumber?.replace(/\D/g, '') || '',
    [],
  );

  const [step, setStep] = useState<Step>('details');
  const [legalName, setLegalName] = useState('');
  const [cpfCnpj, setCpfCnpj] = useState('');
  const [address, setAddress] = useState<BillingAddressValue>({
    postalCode: '',
    addressNumber: '',
    street: '',
    complement: '',
    neighborhood: '',
    city: '',
    state: '',
  });
  const [cardHolderName, setCardHolderName] = useState('');
  const [cardHolderCpf, setCardHolderCpf] = useState('');
  const [cardHolderCpfEdited, setCardHolderCpfEdited] = useState(false);
  const [cardNumber, setCardNumber] = useState('');
  const [cardExpiry, setCardExpiry] = useState('');
  const [cardCcv, setCardCcv] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isPackUpdate, setIsPackUpdate] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await fetchBillingSubscription();
        if (cancelled) return;
        const liveMonthly = (data.subscriptions || []).some((row) => {
          const status = String(row.status || '').toUpperCase();
          const rowInterval = String(row.billingInterval || 'MONTHLY').toUpperCase();
          return (
            interval === 'MONTHLY'
            && rowInterval === 'MONTHLY'
            && ['ACTIVE', 'PAST_DUE', 'GRACE_PERIOD'].includes(status)
            && !row.cancelAtPeriodEnd
          );
        });
        if (liveMonthly) setIsPackUpdate(true);
        const p = data.billingProfile;
        if (p) {
          if (p.legalName) {
            setLegalName(p.legalName);
            setCardHolderName((prev) => prev || p.legalName || '');
          }
          if (p.cpfCnpj) setCpfCnpj(formatCpfCnpj(p.cpfCnpj));
          if (p.postalCode || p.addressNumber || p.addressStreet) {
            setAddress((prev) => ({
              ...prev,
              postalCode: p.postalCode ? formatCep(p.postalCode) : prev.postalCode,
              addressNumber: p.addressNumber || prev.addressNumber,
              street: p.addressStreet || prev.street,
              complement: p.addressComplement || prev.complement,
              neighborhood: p.addressNeighborhood || prev.neighborhood,
              city: p.addressCity || prev.city,
              state: p.addressState || prev.state,
            }));
          }
        }
      } catch {
        /* non-blocking */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [interval]);

  const emailNormalized = verifiedEmail.trim().toLowerCase();
  const pricing = useMemo(() => {
    if (seatCount !== undefined) {
      return packPricing({ seatCount, volumePacks, interval });
    }
    if (seatPacks !== undefined) {
      return packPricing({ seatPacks, volumePacks, interval });
    }
    return packPricing({ seatCount: BASE_TEAM_SEATS, volumePacks, interval });
  }, [seatCount, seatPacks, volumePacks, interval]);
  const amount = pricing.chargeCents;
  const yearlyAmount = packPricing({
    seatCount: pricing.seatCount,
    volumePacks: pricing.volumePacks,
    interval: 'YEARLY',
  }).chargeCents;

  const cardDigits = digitsOnly(cardNumber);
  const cpfDigits = digitsOnly(cpfCnpj);
  const postalDigits = digitsOnly(address.postalCode);
  const phoneDigits = useMemo(() => {
    const d = digitsOnly(authPhone);
    return d.replace(/^55/, '') || d;
  }, [authPhone]);
  const expiry = parseExpiry(cardExpiry);
  const holderName = cardHolderName.trim() || legalName.trim();
  const holderCpfValue = cardHolderCpfEdited ? cardHolderCpf : cpfCnpj;
  const holderCpfDigits = digitsOnly(holderCpfValue);
  const cpfReady = isValidCpfCnpj(cpfDigits);
  const holderCpfReady = isValidCpfCnpj(holderCpfDigits);

  const detailsReady =
    !!legalName.trim() &&
    !!emailNormalized &&
    cpfReady &&
    postalDigits.length === 8 &&
    !!address.addressNumber.trim() &&
    !!address.street.trim() &&
    !!address.city.trim() &&
    address.state.length === 2 &&
    phoneDigits.length >= 10;

  const paymentReady =
    !!holderName &&
    holderCpfReady &&
    cardDigits.length >= 13 &&
    !!expiry &&
    digitsOnly(cardCcv).length >= 3;

  const confirmReady = detailsReady && paymentReady;

  const submit = async () => {
    setError(null);
    const parsed = parseExpiry(cardExpiry);
    if (!parsed) {
      setError('Validade do cartão inválida. Use MM/AA.');
      return;
    }

    setBusy(true);
    try {
      const result = await startCardCheckout({
        legalName: legalName.trim(),
        email: emailNormalized,
        cpfCnpj: cpfDigits,
        phone: phoneDigits,
        billingInterval: interval,
        seatCount: pricing.seatCount,
        volumePacks: pricing.volumePacks,
        creditCard: {
          holderName,
          number: cardDigits,
          expiryMonth: parsed.month,
          expiryYear: parsed.year,
          ccv: digitsOnly(cardCcv),
        },
        creditCardHolderInfo: {
          name: holderName,
          email: emailNormalized,
          cpfCnpj: holderCpfDigits,
          postalCode: postalDigits,
          addressNumber: address.addressNumber.trim(),
          addressComplement: address.complement.trim() || null,
          addressStreet: address.street.trim(),
          addressNeighborhood: address.neighborhood.trim() || null,
          addressCity: address.city.trim(),
          addressState: address.state,
          phone: phoneDigits,
          mobilePhone: phoneDigits,
        },
      });
      invalidateBillingSummaryCache();
      if (result.packUpdated) {
        onPackUpdated?.();
        return;
      }
      onPending(result.subscriptionId);
    } catch (e) {
      setError(callableErrorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const termsUrl = getTermsUrl();
  const privacyUrl = getPrivacyUrl();
  const stepIndex = STEPS.indexOf(step);

  const goBack = () => {
    setError(null);
    if (step === 'details') {
      onBack();
      return;
    }
    setStep(STEPS[stepIndex - 1]);
  };

  const period = interval === 'YEARLY' ? 'ano' : 'mês';
  const extraSeats = Math.max(0, pricing.seatCount - BASE_TEAM_SEATS);
  const stepCta =
    step === 'details'
      ? {
          label: 'Continuar para pagamento',
          ready: detailsReady,
          run: () => {
            setError(null);
            setStep('payment');
          },
        }
      : step === 'payment'
        ? {
            label: 'Revisar e confirmar',
            ready: paymentReady,
            run: () => {
              setError(null);
              setStep('confirm');
            },
          }
        : {
            label: busy
              ? 'Processando…'
              : isPackUpdate
                ? 'Atualizar plano'
                : 'Pagar e assinar',
            ready: confirmReady && !busy,
            run: () => void submit(),
          };

  return (
    <div className="flex h-[100dvh] flex-col overflow-hidden lg:block lg:h-auto lg:min-h-screen lg:overflow-visible">
      <header className="z-20 shrink-0 border-b border-border bg-background/95 px-5 backdrop-blur lg:hidden"
        style={{ paddingTop: 'max(0.75rem, env(safe-area-inset-top))' }}
      >
        <div className="flex items-center justify-between gap-3 pb-3">
          <button
            type="button"
            className="shrink-0 text-left text-sm text-foreground"
            onClick={goBack}
          >
            ← {step === 'details' ? 'Meu plano' : 'Voltar'}
          </button>
          <CheckoutSteps step={step} stepIndex={stepIndex} variant="icons" />
        </div>
      </header>

      <div className="min-h-0 flex-1 overflow-y-auto lg:overflow-visible">
        <div className="mx-auto flex w-full max-w-lg flex-col px-5 py-5 lg:mx-0 lg:grid lg:min-h-screen lg:max-w-none lg:grid-cols-2 lg:px-0 lg:py-0">
      <aside className="relative hidden lg:flex lg:min-h-screen lg:flex-col lg:border-r lg:border-border">
        <div
          className="pointer-events-none absolute inset-0 hidden bg-[#f3f3f1] bg-cover bg-[position:38%_center] lg:block"
          style={{ backgroundImage: "url('/brand/x9-wellcome.png')" }}
          aria-hidden
        />
        <div className="relative z-10 lg:ml-auto lg:flex lg:min-h-screen lg:w-full lg:max-w-md lg:flex-col lg:px-8 lg:py-10 xl:pr-12">
          <button
            type="button"
            className="mb-5 text-left text-sm text-foreground lg:mb-8"
            onClick={goBack}
          >
            ← {step === 'details' ? 'Meu plano' : 'Voltar'}
          </button>
          <div className="lg:sticky lg:top-10 lg:rounded-2xl lg:bg-white/80 lg:p-6 lg:shadow-sm lg:backdrop-blur-md">
            <CheckoutOrderSummary
              interval={interval}
              pricing={pricing}
              amount={amount}
              yearlyAmount={yearlyAmount}
              onSwitchToYearly={onSwitchToYearly}
              isPackUpdate={isPackUpdate}
            />
          </div>
          <div className="mt-auto hidden pt-10 lg:block">
            <HwyahProductFooter className="w-full" />
          </div>
        </div>
      </aside>

      <main className="flex min-w-0 flex-col gap-5 lg:min-h-screen lg:px-8 lg:py-10 xl:pl-12">
        <div className="mx-auto w-full max-w-md space-y-5 lg:mx-0">
      <div className="hidden lg:block">
        <CheckoutSteps step={step} stepIndex={stepIndex} />
        <hr className="mt-5 border-border" />
      </div>

      {step === 'details' && (
        <div className="space-y-4">
          <div>
            <p className="my-label">E-mail da conta (NFS-e / recuperação)</p>
            <div className="relative">
              <p className="rounded-xl border border-border bg-black/[0.03] px-4 py-3 pr-11 text-sm text-foreground">
                {emailNormalized}
              </p>
              <FieldVerifiedIcon />
            </div>
          </div>

          <div>
            <p className="my-label">Celular da conta</p>
            <div className="relative">
              <p className="rounded-xl border border-border bg-black/[0.03] px-4 py-3 pr-11 text-sm text-foreground">
                {phoneDigits ? formatBrPhone(phoneDigits) : 'Celular não encontrado na conta'}
              </p>
              {phoneDigits.length >= 10 ? <FieldVerifiedIcon label="Celular verificado" /> : null}
            </div>
          </div>

          <div>
            <label className="my-label" htmlFor="legalName">
              Nome completo ou razão social
            </label>
            <input
              id="legalName"
              className="my-input"
              autoComplete="name"
              value={legalName}
              onChange={(e) => setLegalName(e.target.value)}
            />
          </div>

          <div>
            <label className="my-label" htmlFor="cpf">
              CPF ou CNPJ
            </label>
            <div className="relative">
              <input
                id="cpf"
                className="my-input pr-11"
                inputMode="numeric"
                autoComplete="off"
                placeholder="000.000.000-00"
                value={cpfCnpj}
                onChange={(e) => setCpfCnpj(formatCpfCnpj(e.target.value))}
              />
              {cpfReady ? <FieldVerifiedIcon label="CPF/CNPJ válido" /> : null}
            </div>
          </div>

          <BillingAddressFields value={address} onChange={setAddress} />

          <div className="hidden lg:block space-y-3">
            <CheckoutError message={error} />
            <hr className="border-border" />
            <button
              type="button"
              className="my-btn-primary"
              disabled={!detailsReady}
              onClick={stepCta.run}
            >
              Continuar para pagamento
            </button>
          </div>
        </div>
      )}

      {step === 'payment' && (
        <div className="space-y-4">
          <div className="space-y-2">
            <p className="text-sm font-medium text-foreground">Cartão de crédito</p>
            <AcceptedCardBrands />
          </div>

          <div>
            <label className="my-label" htmlFor="cardNumber">
              Número do cartão
            </label>
            <input
              id="cardNumber"
              className="my-input"
              inputMode="numeric"
              autoComplete="cc-number"
              placeholder="•••• •••• •••• ••••"
              value={cardNumber}
              onChange={(e) => setCardNumber(formatCardNumber(e.target.value))}
            />
          </div>
          <div>
            <label className="my-label" htmlFor="cardHolder">
              Nome impresso no cartão
            </label>
            <input
              id="cardHolder"
              className="my-input"
              autoComplete="cc-name"
              placeholder={legalName || 'Como no cartão'}
              value={cardHolderName}
              onChange={(e) => setCardHolderName(e.target.value)}
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="my-label" htmlFor="cardExpiry">
                Validade
              </label>
              <input
                id="cardExpiry"
                className="my-input"
                inputMode="numeric"
                autoComplete="cc-exp"
                placeholder="MM/AA"
                value={cardExpiry}
                onChange={(e) => setCardExpiry(formatExpiryInput(e.target.value))}
              />
            </div>
            <div>
              <label className="my-label" htmlFor="cardCcv">
                CVV
              </label>
              <input
                id="cardCcv"
                className="my-input"
                inputMode="numeric"
                autoComplete="cc-csc"
                placeholder="123"
                maxLength={4}
                value={cardCcv}
                onChange={(e) => setCardCcv(digitsOnly(e.target.value).slice(0, 4))}
              />
            </div>
          </div>
          <div>
            <label className="my-label" htmlFor="cardHolderCpf">
              CPF do titular do cartão
            </label>
            <div className="relative">
              <input
                id="cardHolderCpf"
                className="my-input pr-11"
                inputMode="numeric"
                autoComplete="off"
                placeholder="000.000.000-00"
                value={holderCpfValue}
                onChange={(e) => {
                  setCardHolderCpfEdited(true);
                  setCardHolderCpf(formatCpfCnpj(e.target.value));
                }}
              />
              {holderCpfReady ? <FieldVerifiedIcon label="CPF/CNPJ válido" /> : null}
            </div>
            <p className="mt-1 text-xs text-muted-foreground">
              Deve ser o CPF (ou CNPJ) cadastrado no cartão. Pode ser diferente da cobrança.
            </p>
          </div>
          <div className="hidden lg:block space-y-3">
            <CheckoutError message={error} />
            <hr className="border-border" />
            <button
              type="button"
              className="my-btn-primary"
              disabled={!paymentReady}
              onClick={stepCta.run}
            >
              Revisar e confirmar
            </button>
          </div>
        </div>
      )}

      {step === 'confirm' && (
        <div className="space-y-4">
          <p className="text-sm font-semibold text-foreground">Confira os dados</p>

          <div className="my-card space-y-1 text-sm">
            <p className="font-semibold text-foreground">Usuário</p>
            <p className="text-muted-foreground">{legalName.trim()}</p>
            <p className="text-muted-foreground">{emailNormalized}</p>
            {phoneDigits ? (
              <p className="text-muted-foreground">{formatBrPhone(phoneDigits)}</p>
            ) : null}
            <p className="text-muted-foreground">
              {cpfDigits.length > 11 ? 'CNPJ' : 'CPF'} {formatCpfCnpj(cpfDigits)}
            </p>
          </div>

          <div className="my-card space-y-1 text-sm">
            <p className="font-semibold text-foreground">Endereço de cobrança</p>
            <p className="text-muted-foreground">
              {[
                address.street.trim(),
                address.addressNumber.trim(),
                address.complement.trim(),
              ].filter(Boolean).join(', ')}
              {address.neighborhood.trim() ? ` — ${address.neighborhood.trim()}` : ''}
            </p>
            <p className="text-muted-foreground">
              {address.city.trim()} / {address.state} · CEP {formatCep(postalDigits)}
            </p>
          </div>

          <div className="my-card space-y-2 text-sm">
            <p className="font-semibold text-foreground">Pagamento</p>
            <p className="text-muted-foreground">
              Cartão •••• {cardDigits.slice(-4)} · {cardExpiry}
            </p>
            <p className="text-muted-foreground">
              CPF do titular {formatCpfCnpj(holderCpfDigits)}
            </p>
            <AsaasSecureNote />
          </div>

          <div className="space-y-3 pt-1">
            <p className="text-xs leading-snug text-muted-foreground">
              A cobrança no cartão será feita por Happy When You Are Happy Ltda.
              Ao pagar, você autoriza a cobrança recorrente no cartão. Os{' '}
              <a className="text-foreground underline" href={termsUrl} target="_blank" rel="noreferrer">
                termos de uso
              </a>{' '}
              e a{' '}
              <a
                className="text-foreground underline"
                href={privacyUrl}
                target="_blank"
                rel="noreferrer"
              >
                política de privacidade
              </a>{' '}
              já foram aceitos na sua conta.
            </p>
            <HwyahProductFooter align="start" className="w-full lg:hidden" />
          </div>

          <div className="hidden lg:block space-y-3">
            <CheckoutError message={error} />
            <hr className="border-border" />
            <button
              type="button"
              className="my-btn-primary"
              disabled={busy || !confirmReady}
              onClick={stepCta.run}
            >
              {stepCta.label}
            </button>
          </div>
        </div>
      )}
        </div>
      </main>
        </div>
      </div>

      <footer
        className="z-20 shrink-0 bg-transparent px-4 pt-2 lg:hidden"
        style={{ paddingBottom: 'max(0.75rem, env(safe-area-inset-bottom))' }}
      >
        <div className="rounded-2xl bg-background px-4 py-3 shadow-[0_10px_28px_rgba(15,23,42,0.12)] ring-1 ring-black/[0.04]">
          <div className="mb-3 flex items-baseline justify-between gap-3">
            <div className="min-w-0">
              <p className="truncate text-xs text-muted-foreground">
                X9 Equipe · {interval === 'YEARLY' ? 'anual' : 'mensal'}
                {extraSeats > 0 ? ` · ${pricing.seatCount} assentos` : ''}
              </p>
              <p className="text-base font-bold text-foreground">
                {brl(amount)}
                <span className="ml-1 text-sm font-medium text-muted-foreground">
                  /{period}
                </span>
              </p>
            </div>
            {interval === 'MONTHLY' ? (
              <button
                type="button"
                className="shrink-0 text-xs font-semibold text-foreground underline underline-offset-2"
                onClick={onSwitchToYearly}
              >
                Ir ao anual
              </button>
            ) : null}
          </div>
          <CheckoutError message={error} className="mb-3" />
          <button
            type="button"
            className="my-btn-primary"
            disabled={!stepCta.ready}
            onClick={stepCta.run}
          >
            {stepCta.label}
          </button>
        </div>
      </footer>
    </div>
  );
}
